import { data as json, type MetaFunction, Link } from 'react-router'
import { CloudflareEmailOff } from '#app/components/cloudflare-email-off.tsx'
import { Grid } from '#app/components/grid.tsx'
import { GithubIcon, LinkedInIcon, XIcon } from '#app/components/icons.tsx'
import { HeroSection } from '#app/components/sections/hero-section.tsx'
import { H6, Paragraph } from '#app/components/typography.tsx'
import { externalLinks } from '#app/external-links.tsx'
import { type KCDHandle } from '#app/types.ts'
import { type Route } from './+types/connect'

export const handle: KCDHandle = {
	getSitemapEntries: () => [{ route: '/connect' }],
}

export async function loader({ request }: Route.LoaderArgs) {
	return json({
		socialMetas: (
			await import('#app/og/page-meta.server.ts')
		).buildPageSocialMetasForRequest(request, {
			title: 'Connect with Hamidul Islam',
			description:
				'Come check out how Hamidul Islam can help you grow your product.',
			keywords: 'Hamidul Islam, HamidX, schedule a call, product engineering',
			image: '/images/heroes/connect.jpg',
			imageAlt: 'Connect with Hamidul Islam',
		}),
	})
}

export const meta: MetaFunction<typeof loader> = ({ data }) =>
	data?.socialMetas ?? []

const links = [
	{ name: 'Schedule a call', href: externalLinks.cal, external: true },
	{ name: 'Email', href: externalLinks.email, external: true },
	{ name: 'Work', href: '/work' },
	{ name: 'Resume', href: '/resume' },
	{ name: 'About', href: '/about' },
	{ name: 'Blog', href: '/blog' },
	{ name: 'Contact', href: '/contact' },
]

export default function ConnectRoute() {
	return (
		<>
			<HeroSection
				title="Let's grow your product."
				subtitle="I help teams ship systems that remove friction, scale under load, and turn into revenue."
				imageProps={{
					src: '/images/heroes/connect.png',
					alt: 'Connection network illustration',
					className: 'object-contain',
					style: { aspectRatio: '1' },
				}}
				imageSize="medium"
			/>

			<Grid as="main" className="mb-48">
				<div className="col-span-full lg:col-span-5">
					<img
						src="/images/hamid-portrait.webp"
						alt="Portrait of Hamidul Islam"
						width={768}
						height={1024}
						className="mx-auto aspect-[3/4] w-full max-w-[18rem] rounded-lg object-cover object-[center_20%] lg:mx-0"
						loading="lazy"
						decoding="async"
					/>
					<p className="text-primary mt-6 text-center text-2xl font-medium lg:text-left">
						Hamid
					</p>
					<Paragraph className="mt-6">
						Product engineer for businesses that need speed, reliability, and
						clear ownership - from customer-facing apps to systems that quietly
						handle the hard parts.
					</Paragraph>
					<div className="text-secondary mt-6 flex items-center gap-4">
						<a href={externalLinks.github} aria-label="GitHub">
							<GithubIcon size={28} />
						</a>
						<a href={externalLinks.x} aria-label="X / Twitter">
							<XIcon size={28} />
						</a>
						<a href={externalLinks.linkedin} aria-label="LinkedIn">
							<LinkedInIcon size={28} />
						</a>
					</div>
				</div>

				<div className="col-span-full mt-12 lg:col-span-5 lg:col-start-8 lg:mt-0">
					<H6 as="h2" className="mb-6">
						Quick links
					</H6>
					<ul className="space-y-1">
						{links.map((item) => {
							const className =
								'text-secondary underlined hover:text-team-current focus:text-team-current inline-block py-2 text-xl focus:outline-none'
							const link = item.external ? (
								<a href={item.href} className={className}>
									{item.name}
								</a>
							) : (
								<Link prefetch="intent" to={item.href} className={className}>
									{item.name}
								</Link>
							)
							return (
								<li key={item.href}>
									{item.href.startsWith('mailto:') ? (
										<CloudflareEmailOff>{link}</CloudflareEmailOff>
									) : (
										link
									)}
								</li>
							)
						})}
					</ul>
				</div>
			</Grid>
		</>
	)
}
